import styled, { css } from 'styled-components';
import { Typography } from '~/ui/components';
import { themeColor } from '~/common/utils/theme';

export interface LoadingProp {
  loading?: boolean | 1 | 0;
}

export const TableContainer = styled.table<LoadingProp>`
  width: 100%;

  ${({ loading }) =>
    loading &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;

export const Thead = styled.thead`
  background-color: ${themeColor('background.primary', { dark: 'background.secondary' })};
  height: 34px;
  position: ${({ $isTHeadFixed }) => ($isTHeadFixed ? 'sticky' : 'none')};
  top: ${({ $isTHeadFixed }) => ($isTHeadFixed ? '-2px' : 'none')};
  z-index: ${({ $isTHeadFixed }) => ($isTHeadFixed ? 1 : 'none')};

  tr {
    th {
      font-size: ${({ theme }) => theme.typography.types.footnote.size};
      line-height: ${({ theme }) => theme.typography.types.footnote.lineHeight};
      font-family: ${({ theme }) => theme.typography.fonts[theme.typography.defaultFamily]};
      font-weight: 500;
      color: ${themeColor('element.secondary', { dark: 'element.secondary' })};

      &:not(:last-child) {
        padding-right: 21px;
      }

      &.cursor-pointer {
        cursor: pointer;
      }

      .arrow {
        margin-left: 8px;
      }

      @media (max-width: 1280px) {
        font-size: ${({ theme }) => theme.typography.types.footnote.size};

        &:not(:last-child) {
          padding-right: 16px;
        }
      }
    }
  }
`;

type TbodyProps = {
  rowHeight?: number;
  clickable?: boolean;
  $timeline?: boolean;
};

export const Tbody = styled.tbody<TbodyProps>`
  tr {
    position: relative;
    border-top: 1px ${({ $withoutBorders }) => ($withoutBorders ? 'none' : 'solid')}
      ${({ theme }) => theme.colors.background.primary};
    height: ${({ rowHeight }) => rowHeight || 76}px;
    background-color: ${themeColor('background.primary', { dark: 'background.secondary' })};

    &.grouped {
      height: 36px;

      & + .sub-row {
        border-top: none;
      }
    }

    .td-sub-rows {
      padding-right: 16px !important;

      button {
        background-color: transparent;
        cursor: pointer !important;

        svg {
          color: ${({ theme }) => theme.colors.brand.primary};
        }
      }

      svg {
        color: ${({ theme }) => theme.colors.neutral.primary};
      }
    }

    &:first-child {
      border-top: none;
    }

    ${({ clickable }) =>
      clickable &&
      css`
        &:hover {
          opacity: 0.5;
          cursor: pointer;
        }
      `}

    ${({ $timeline }) =>
      $timeline &&
      css`
        border: none;
        border-left: 2px solid ${({ theme }) => theme.colors.element.placeholder};
        height: 100px !important;

        td:first-child {
          padding-left: 19px;
        }
      `}

    td {
      font-size: ${({ theme }) => theme.typography.types.caption.size};
      line-height: ${({ theme }) => theme.typography.types.caption.lineHeight};
      font-family: ${({ theme }) => theme.typography.fonts[theme.typography.defaultFamily]};
      font-weight: 400;
      color: ${themeColor('element.primary')};

      &:not(:last-child) {
        padding-right: 21px;
      }

      &.td-sub-rows {
        padding-right: 8px;
        width: 40px;
        padding-left: 8px;
      }

      .table-link {
        color: ${({ theme }) => theme.colors.brand.primary};
        text-decoration: none;
        outline: none;
        cursor: pointer;
        transition: color 0.3s;
      }

      .td-grouped {
        height: 40px !important;
      }

      @media (max-width: 1280px) {
        font-size: ${({ theme }) => theme.typography.types.caption.size};

        &:not(:last-child) {
          padding-right: 16px;
        }
      }
    }

    //&.sub-row {
    //  border: none;
    //}
  }
`;

export const NotFoundMessage = styled.p`
  height: 86px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  color: ${themeColor('element.primary')};
  background: ${themeColor('background.primary', { dark: 'background.secondary' })};
  gap: 8px;
`;

export type AlignType = 'left' | 'center' | 'right';

export type CellProps = {
  align?: AlignType;
  minSize?: number;
  maxSize?: number;
  ellipsis?: boolean;
};

export const GroupedCell = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.element.secondary};
  font-weight: bold;
`;

export const Cell = styled.div<CellProps>`
  display: flex;
  justify-content: ${({ align }) => align || 'left'};
  align-items: center;
  gap: 8px;

  min-width: ${({ minSize }) => minSize || 0}px;
  max-width: ${({ maxSize }) => maxSize || 0}px;

  .depth-icon {
    color: ${({ theme }) => theme.colors.info.primary};
    width: 16px;
    height: 16px;
  }

  ${({ ellipsis }) =>
    ellipsis &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    `}
`;

function getMaxHeight(maxHeight: number | string | undefined) {
  if (typeof maxHeight === 'number') {
    return `${maxHeight}px`;
  }
  if (typeof maxHeight === 'string') {
    return maxHeight;
  }
  return 'initial';
}

type TableWrapperProps = {
  noPadding?: boolean;
  hasSubRows?: boolean;
  zeroMargin?: boolean;
};

export const TableWrapper = styled.div.attrs({
  className: 'table-wrapper',
})<TableWrapperProps>`
  margin: ${({ zeroMargin }) => (zeroMargin ? '0' : '24px 0')};
  padding: ${({ noPadding, hasSubRows }) => (noPadding || hasSubRows ? '0' : '0 28px')};
  background-color: ${themeColor('background.primary', { dark: 'background.secondary' })};
  max-height: ${({ $maxHeight }) => getMaxHeight($maxHeight)};
  overflow: auto;
  width: 100%;
`;

export const ToggleButton = styled.button`
  height: 24px;
  min-width: 40px;
  width: 40px;
  background: ${themeColor('info.secondary')};
  border: none;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    color: ${themeColor('info.primaryAlt')};
    width: 10px;
    height: 10px;
  }
`;

export const ResponsiveWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 32px;
  padding: 16px 8px;
`;

export const RespCol = styled.div`
  gap: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const RespHeader = styled(Typography).attrs(({ theme }) => ({
  type: 'caption',
  element: 'span',
  color: theme.colors.element.secondary,
}))``;

export const RespValue = styled(Typography).attrs(({ theme }) => ({
  type: 'caption',
  element: 'span',
  color: theme.colors.element.primary,
  weight: 600,
}))`
  min-height: 48px;
  display: flex;
  align-items: center;
`;

export const Wrapper = styled.div`
  background-color: ${themeColor('background.primary')};
  display: flex;
  width: 100%;
`;

export const DotContent = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 12px;
  min-height: 12px;
  min-width: 12px;
  background-color: ${themeColor('background.primary')};
  left: -8px;
  top: -6px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 8px;
  min-height: 8px;
  min-width: 8px;
  background-color: ${themeColor('element.placeholder')};
`;
